<script lang="ts" setup>
import { onUnmounted, watch, ref } from 'vue'
import { storeToRefs } from 'pinia'

import { useShipStore } from '@/stores/ship'

import { HelpTextTooltip, TextField } from '@haskoning/vue-component-library'
import ChoiceField from '@/components/form/ChoiceField.vue'

const shipStore = useShipStore()
const { currentItem, createUpdateDialog, inputFields, writeLoading, fields } =
  storeToRefs(shipStore)

const formRef = ref()
const isValid = ref(false)

const hasHelpText = (field: string) => {
  return !!fields.value?.[field]?.help_text
}

const getFieldType = (field: string) => {
  return fields.value?.[field]?.type
}

const emit = defineEmits(['save'])

watch(createUpdateDialog, (value) => {
  if (!value) {
    shipStore.setCurrentItem(null)
  }
})

const save = async () => {
  await formRef.value.validate()
  if (isValid.value) {
    emit('save')
  }
}

onUnmounted(() => {
  shipStore.setCurrentItem(null)
})
</script>

<template>
  <v-dialog v-model="createUpdateDialog" width="120vh" persistent>
    <v-card class="pa-3">
      <v-card-title>{{ currentItem?.id ? 'Edit' : 'Create' }} ship</v-card-title>
      <div v-if="fields" class="overflow-y-auto" style="height: 80%">
        <v-form v-model="isValid" ref="formRef">
          <v-row
            class="center px-4 overflow-y-auto"
            no-gutters
            v-for="field in inputFields"
            :key="field"
          >
            <v-col cols="12" md="6" lg="6" class="d-flex align-center">
              <HelpTextTooltip v-if="hasHelpText(field)" :text="fields[field].help_text || ''" />
              <span :class="hasHelpText(field) ? 'px-2' : 'px-0'">
                {{ fields[field].label }}
              </span>
            </v-col>

            <v-col cols="12" md="6" lg="6" class="pt-0">
              <template v-if="getFieldType(field) === 'choice'">
                <ChoiceField
                  :field="fields[field]"
                  :model-value="currentItem"
                  :no-help-text="true"
                  :name="`${field}-choice`"
                />
              </template>
              <template v-else>
                <TextField
                  :field="fields[field]"
                  :model-value="currentItem"
                  :no-help-text="true"
                  :name="`${field}-text`"
                />
              </template>
            </v-col>
          </v-row>
        </v-form>
      </div>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          :disabled="writeLoading"
          color="primary"
          class="text-none"
          variant="text"
          @click="shipStore.closeCreateUpdateDialog()"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          class="text-none"
          variant="text"
          :loading="writeLoading"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
